import { useState } from "react";
import {
  FILE_KINDS, KINDS, KIND_LABELS, KIND_SHORT, PROOFS, PROOF_LABELS, STATUS_LABELS, STATUS_VALUES, changeKind, conditionTargets, conditionValues, isVisible,
} from "../../lib/procedures";
import ChecklistItemField from "./ChecklistItemField";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Switch } from "../ui/switch";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { ArrowDown, ArrowUp, ChevronDown, ChevronRight, GitBranch, Plus, Trash2, X } from "lucide-react";

const NONE = "__none__";

// The procedure (or report template) as the person on site will see it: answers stay local, they only make the conditions visible.
export function ProcedurePreview({ sections, testid = "procedure-preview", textMax }) {
  const [answers, setAnswers] = useState({});
  return (
    <div className="mx-auto w-full max-w-[400px] rounded-2xl border border-slate-200 bg-white shadow-sm" data-testid={testid}>
      <div className="border-b border-slate-100 px-4 py-2 text-xs text-slate-500">Aperçu sur téléphone (rien n'est enregistré)</div>
      <div className="space-y-6 p-4">
        {sections.length === 0 && <p className="text-sm text-slate-500">Aucune section pour le moment.</p>}
        {sections.map((section) => (
          <section key={section.id} className="space-y-4">
            <h4 className="text-sm font-semibold uppercase tracking-wide text-slate-500">{section.title || "Section sans titre"}</h4>
            {section.items.filter((item) => isVisible(item, answers)).map((item) => (
              <ChecklistItemField key={item.id} item={item} value={answers[item.id]} onChange={(v) => setAnswers((a) => ({ ...a, [item.id]: v }))}
                testid={`preview-item-${item.id}`} {...(textMax ? { maxLength: textMax } : {})} />
            ))}
          </section>
        ))}
      </div>
    </div>
  );
}

// A published or replaced version, read at a glance: every section and item with the rules a phone preview cannot show (hidden
// conditions, bounds, proofs, anomalies). Read-only; the preview next to it lets the reader try the conditions.
export function ProcedureOutline({ sections, checklist = true }) {
  const all = sections.flatMap((s) => s.items);
  const labelOf = (id) => all.find((i) => i.id === id)?.label || "un élément précédent";
  if (!sections.length) return <p className="text-sm text-slate-500">Aucune section dans cette version.</p>;
  return (
    <div className="space-y-4" data-testid="procedure-outline">
      {sections.map((section, sIndex) => (
        <section key={section.id} className="rounded-lg border border-slate-200 bg-white">
          <div className="flex items-center justify-between gap-2 border-b border-slate-100 px-4 py-3">
            <h2 className="text-sm font-semibold text-slate-900">{section.title || "Section sans titre"}</h2>
            <span className="shrink-0 text-xs text-slate-500">Section {sIndex + 1} · {section.items.length} élément{section.items.length > 1 ? "s" : ""}</span>
          </div>
          <ol className="divide-y divide-slate-100">
            {section.items.map((item) => {
              const rules = outlineRules(item, checklist);
              return (
                <li key={item.id} className="px-4 py-3 space-y-1" data-testid={`outline-item-${item.id}`}>
                  <div className="flex items-start gap-2 flex-wrap">
                    <span className="text-sm font-medium text-slate-900 break-words min-w-0">{item.label || "Élément sans libellé"}</span>
                    <span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">{KIND_SHORT[item.kind]}</span>
                    <span className={`shrink-0 text-xs ${item.required ? "text-slate-700" : "text-slate-400"}`}>{item.required ? "Obligatoire" : "Facultatif"}</span>
                  </div>
                  {item.help && <p className="text-sm text-slate-500 whitespace-pre-line">{item.help}</p>}
                  {item.condition && (
                    <p className="text-xs text-slate-600 inline-flex items-center gap-1">
                      <GitBranch size={12} className="shrink-0 text-slate-400" />
                      Affiché seulement si « {labelOf(item.condition.item_id)} » vaut « {STATUS_LABELS[item.condition.equals] || item.condition.equals} »
                    </p>
                  )}
                  {rules.length > 0 && <p className="text-xs text-slate-500">{rules.join(" · ")}</p>}
                </li>
              );
            })}
            {section.items.length === 0 && <li className="px-4 py-3 text-sm text-slate-400">Aucun élément.</li>}
          </ol>
        </section>
      ))}
    </div>
  );
}

function outlineRules(item, checklist) {
  const rules = [];
  if (item.kind === "choice") rules.push(`Choix : ${item.options.filter(Boolean).join(", ")}`);
  if (item.kind === "number") {
    const unit = item.unit ? ` ${item.unit}` : "";
    const hasMin = item.min !== null && item.min !== undefined;
    const hasMax = item.max !== null && item.max !== undefined;
    if (hasMin && hasMax) rules.push(`Attendu entre ${item.min} et ${item.max}${unit}`);
    else if (hasMin) rules.push(`Attendu ≥ ${item.min}${unit}`);
    else if (hasMax) rules.push(`Attendu ≤ ${item.max}${unit}`);
    else if (item.unit) rules.push(`Unité : ${item.unit}`);
  }
  if (!checklist) return rules;
  if (item.kind === "status" && item.anomaly_values?.length) rules.push(`Anomalie si ${item.anomaly_values.map((v) => STATUS_LABELS[v]).join(" ou ")}`);
  if (item.kind === "choice" && item.anomaly_values?.length) rules.push(`Anomalie si ${item.anomaly_values.join(" ou ")}`);
  if (item.proof && item.proof !== "none") rules.push(PROOF_LABELS[item.proof]);
  if (item.estimated_minutes) rules.push(`≈ ${item.estimated_minutes} min`);
  return rules;
}

const numberOrNull = (value) => (value === "" || value === null || value === undefined ? null : value);

// One item of a draft, editable. Collapsed it shows a one-line summary; open it shows only the fields its kind uses.
// `checklist` false (a report template): no anomaly, expected range, proof or duration — they only mean something in a checklist.
export function ItemEditor({ item, sections, open, onToggle, onChange, onRemove, onMove, canMoveUp, canMoveDown, problem, checklist = true }) {
  const set = (patch) => onChange({ ...item, ...patch });
  const targets = conditionTargets(sections, item.id);
  const target = targets.find((t) => t.id === item.condition?.item_id);
  const tid = `item-editor-${item.id}`;

  return (
    <div className={`rounded-lg border ${problem ? "border-amber-400" : "border-slate-200"} bg-white`} data-testid={tid}>
      <div className="flex items-center gap-2 px-3 py-2">
        <button type="button" onClick={onToggle} className="flex min-w-0 flex-1 items-center gap-2 text-left" aria-expanded={open} data-testid={`${tid}-toggle`}>
          {open ? <ChevronDown size={15} className="shrink-0 text-slate-400" /> : <ChevronRight size={15} className="shrink-0 text-slate-400" />}
          <span className={`truncate text-sm ${item.label ? "text-slate-900" : "italic text-slate-400"}`}>{item.label || "Élément sans libellé"}</span>
          <span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">{KIND_SHORT[item.kind]}</span>
          {item.required && <span className="hidden shrink-0 text-xs text-slate-500 sm:inline">obligatoire</span>}
          {item.condition && <GitBranch size={13} className="shrink-0 text-slate-400" aria-label="conditionnel" />}
        </button>
        <Button type="button" variant="ghost" size="icon" className="h-8 w-8" aria-label="Monter" disabled={!canMoveUp} onClick={() => onMove(-1)}><ArrowUp size={14} /></Button>
        <Button type="button" variant="ghost" size="icon" className="h-8 w-8" aria-label="Descendre" disabled={!canMoveDown} onClick={() => onMove(1)}><ArrowDown size={14} /></Button>
        <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-slate-500 hover:text-red-700" aria-label="Retirer l'élément" onClick={onRemove}
          data-testid={`${tid}-remove`}><Trash2 size={14} /></Button>
      </div>
      {problem && <p className="px-3 pb-2 text-xs text-amber-800">{problem}</p>}
      {open && (
        <div className="space-y-4 border-t border-slate-100 p-3">
          <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_14rem]">
            <div className="space-y-1.5"><Label htmlFor={`${tid}-label`}>Libellé *</Label>
              <Input id={`${tid}-label`} maxLength={200} className="text-base md:text-sm" value={item.label}
                placeholder={checklist ? "Ex. Imprimante ticket branchée et testée" : "Ex. Nombre de postes installés"}
                onChange={(e) => set({ label: e.target.value })} data-testid={`${tid}-label`} /></div>
            <div className="space-y-1.5"><Label>Type de réponse</Label>
              <Select value={item.kind} onValueChange={(kind) => onChange(changeKind(item, kind))}>
                <SelectTrigger data-testid={`${tid}-kind`}><SelectValue /></SelectTrigger>
                <SelectContent>{KINDS.map((k) => <SelectItem key={k} value={k}>{KIND_LABELS[k]}</SelectItem>)}</SelectContent>
              </Select></div>
          </div>
          <div className="space-y-1.5"><Label htmlFor={`${tid}-help`}>Aide (facultatif)</Label>
            <Textarea id={`${tid}-help`} rows={2} maxLength={1000} className="text-base md:text-sm" value={item.help || ""} placeholder="Ce qu'il faut vérifier, comment le faire."
              onChange={(e) => set({ help: e.target.value })} /></div>

          {item.kind === "choice" && (
            <div className="space-y-2">
              <Label>Options</Label>
              {item.options.map((option, index) => (
                <div key={index} className="flex items-center gap-2">
                  <Input className="text-base md:text-sm" maxLength={80} value={option} placeholder={`Option ${index + 1}`} data-testid={`${tid}-option-${index}`}
                    onChange={(e) => set({ options: item.options.map((o, i) => (i === index ? e.target.value : o)),
                      anomaly_values: item.anomaly_values.filter((a) => a !== option) })} />
                  {checklist && (
                    <label className="flex shrink-0 items-center gap-1.5 text-xs text-slate-600">
                      <Switch checked={item.anomaly_values.includes(option) && !!option} disabled={!option}
                        onCheckedChange={(on) => set({ anomaly_values: on ? [...item.anomaly_values, option] : item.anomaly_values.filter((a) => a !== option) })} />
                      anomalie
                    </label>
                  )}
                  <Button type="button" variant="ghost" size="icon" className="h-8 w-8" aria-label="Retirer l'option" disabled={item.options.length <= 2}
                    onClick={() => set({ options: item.options.filter((_, i) => i !== index), anomaly_values: item.anomaly_values.filter((a) => a !== option) })}><X size={14} /></Button>
                </div>
              ))}
              {item.options.length < 20 && (
                <Button type="button" variant="outline" size="sm" onClick={() => set({ options: [...item.options, ""] })} data-testid={`${tid}-option-add`}>
                  <Plus size={14} className="mr-1.5" />Ajouter une option
                </Button>
              )}
            </div>
          )}

          {checklist && item.kind === "status" && (
            <div className="space-y-1.5">
              <Label>Réponses à signaler comme anomalie</Label>
              <div className="flex flex-wrap gap-3">
                {STATUS_VALUES.filter((v) => v !== "ok").map((v) => (
                  <label key={v} className="flex items-center gap-1.5 text-sm text-slate-700">
                    <Switch checked={item.anomaly_values.includes(v)}
                      onCheckedChange={(on) => set({ anomaly_values: on ? [...item.anomaly_values, v] : item.anomaly_values.filter((a) => a !== v) })} />
                    {STATUS_LABELS[v]}
                  </label>
                ))}
              </div>
            </div>
          )}

          {item.kind === "number" && !checklist && (
            <div className="space-y-1.5 max-w-[12rem]"><Label htmlFor={`${tid}-unit`}>Unité</Label>
              <Input id={`${tid}-unit`} maxLength={20} className="text-base md:text-sm" placeholder="postes, heures…" value={item.unit || ""} onChange={(e) => set({ unit: e.target.value })} /></div>
          )}

          {item.kind === "number" && checklist && (
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5"><Label htmlFor={`${tid}-unit`}>Unité</Label>
                <Input id={`${tid}-unit`} maxLength={20} className="text-base md:text-sm" placeholder="V, Mb/s…" value={item.unit || ""} onChange={(e) => set({ unit: e.target.value })} /></div>
              <div className="space-y-1.5"><Label htmlFor={`${tid}-min`}>Minimum</Label>
                <Input id={`${tid}-min`} type="number" step="any" className="text-base md:text-sm" value={item.min ?? ""} onChange={(e) => set({ min: numberOrNull(e.target.value) })} /></div>
              <div className="space-y-1.5"><Label htmlFor={`${tid}-max`}>Maximum</Label>
                <Input id={`${tid}-max`} type="number" step="any" className="text-base md:text-sm" value={item.max ?? ""} onChange={(e) => set({ max: numberOrNull(e.target.value) })} /></div>
              <p className="col-span-3 text-xs text-slate-500">Une mesure hors de ces bornes est signalée comme anomalie.</p>
            </div>
          )}

          <div className="grid gap-3 sm:grid-cols-3">
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <Switch checked={item.required} onCheckedChange={(required) => set({ required })} data-testid={`${tid}-required`} />Obligatoire
            </label>
            {checklist && !FILE_KINDS.includes(item.kind) && (
              <div className="space-y-1.5"><Label>Preuve</Label>
                <Select value={item.proof} onValueChange={(proof) => set({ proof })}>
                  <SelectTrigger data-testid={`${tid}-proof`}><SelectValue /></SelectTrigger>
                  <SelectContent>{PROOFS.map((p) => <SelectItem key={p} value={p}>{PROOF_LABELS[p]}</SelectItem>)}</SelectContent>
                </Select></div>
            )}
            {checklist && (
              <div className="space-y-1.5"><Label htmlFor={`${tid}-minutes`}>Durée estimée (min)</Label>
                <Input id={`${tid}-minutes`} type="number" min={1} max={1440} className="text-base md:text-sm" value={item.estimated_minutes ?? ""}
                  onChange={(e) => set({ estimated_minutes: e.target.value === "" ? null : Math.max(1, Math.min(1440, parseInt(e.target.value, 10) || 1)) })} /></div>
            )}
          </div>

          <div className="space-y-1.5">
            <Label>Afficher seulement si…</Label>
            {targets.length === 0 ? (
              <p className="text-xs text-slate-500">Aucun statut ou choix placé avant cet élément : il est toujours affiché.</p>
            ) : (
              <div className="grid gap-2 sm:grid-cols-2">
                <Select value={item.condition?.item_id || NONE} onValueChange={(v) => set({ condition: v === NONE ? null : { item_id: v, equals: conditionValues(targets.find((t) => t.id === v))[0]?.value || "" } })}>
                  <SelectTrigger data-testid={`${tid}-condition-item`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>Toujours afficher</SelectItem>
                    {targets.map((t) => <SelectItem key={t.id} value={t.id}>{t.label || "Élément sans libellé"}</SelectItem>)}
                  </SelectContent>
                </Select>
                {target && (
                  <Select value={item.condition.equals} onValueChange={(equals) => set({ condition: { ...item.condition, equals } })}>
                    <SelectTrigger data-testid={`${tid}-condition-value`}><SelectValue /></SelectTrigger>
                    <SelectContent>{conditionValues(target).map((v) => <SelectItem key={v.value} value={v.value}>vaut « {v.label} »</SelectItem>)}</SelectContent>
                  </Select>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
